import { createPluginFactory, ELEMENT_PARAGRAPH, ELEMENT_TABLE, ELEMENT_TD } from '@udecode/plate';
import { Editor, Transforms } from 'slate';

import { MAX_TABLE_COLUMNS } from 'components/text_editor_new/lib/table_constants';

// Keeps pasted / edited tables rectangular. Plate 21 models only colSpan (not rowSpan), and its own
// table normalizer doesn't enforce equal-width rows — so a table pasted with vertically-merged cells,
// or one left short by a row/column edit, ends up with rows of differing column counts, which Plate's
// colSpan-indexed row/column transforms then corrupt. This normalizer pads every short row with empty
// cells so the grid is always rectangular and safe to edit.
export function createTableNormalizationPlugin() {
  return createPluginFactory({
    key: 'tableNormalization',
    withOverrides: withTableNormalization,
  })();
}

function withTableNormalization(editor) {
  const { normalizeNode } = editor;

  editor.normalizeNode = entry => {
    const [node, path] = entry;
    if (node.type === ELEMENT_TABLE && padRaggedRows(editor, node, path)) {
      return; // Padded in one pass; return so Slate revalidates the now-rectangular table.
    }
    normalizeNode(entry);
  };

  return editor;
}

// Pads every row narrower than the table's widest row up to that width, all in one pass. Doing every row
// in a single normalizeNode call (rather than one-per-pass) keeps this linear in the number of rows — a
// paste of many short rows would otherwise trigger O(rows) full-tree re-normalizations. Each row is padded
// at its own end, so the inserts are independent and don't shift one another's paths. Returns true when it
// changed anything. Padding only ever fills up to the current max width, so normalization terminates.
function padRaggedRows(editor, table, tablePath) {
  const rows = table.children || [];
  const widths = rows.map(rowWidth);
  // Cap the target width: a row already at/over the cap is left alone rather than padded, so a crafted
  // colSpan can't drive unbounded cell creation.
  const maxWidth = Math.min(MAX_TABLE_COLUMNS, Math.max(0, ...widths));

  let padded = false;
  Editor.withoutNormalizing(editor, () => {
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const deficit = maxWidth - widths[rowIndex];
      if (deficit > 0) {
        const cells = Array.from({ length: deficit }, emptyCell);
        Transforms.insertNodes(editor, cells, { at: [...tablePath, rowIndex, rows[rowIndex].children.length] });
        padded = true;
      }
    }
  });

  return padded;
}

function rowWidth(row) {
  return (row.children || []).reduce((width, cell) => width + (cell.colSpan || 1), 0);
}

function emptyCell() {
  return { type: ELEMENT_TD, children: [{ type: ELEMENT_PARAGRAPH, children: [{ text: '' }] }] };
}
